/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import { detect } from 'detect-browser';
import { type RefObject, useMemo, useRef, useState } from 'react';
import {
  type DropEvent,
  type DropzoneOptions,
  type FileRejection,
  useDropzone,
} from 'react-dropzone';
import { useResizeObserver } from 'usehooks-ts';

import { type CommonProps } from '../_common/types';
import { Banner } from '../banner';
import {
  DocumentArrowUpIconOutline,
  DocumentArrowUpIconSolid,
  XCircleIconSolid,
} from '../icons';
import { ProgressBar } from '../progress-bar';
import { Typography } from '../typography';

/**
 *
 *
 * Types
 *
 *
 */
type DropzoneProps = {
  /** Props for the underlying `react-dropzone` component. Don't use `accept`. If using custom file extensions as this option is broken. Instead use the `acceptedFileExtensions` property. */
  dropZoneOptions: DropzoneOptions;

  /** Informational text that highlights the supported file types, will be displayed in the footer. */
  supportedFilesDescription?: React.ReactNode;

  /** Element to display when uploading the file.*/
  loadingElement?: React.ReactNode;

  /** Use when wanting a custom heading. */
  heading?: React.ReactNode;

  /** Message to be shown when file is rejected. */
  rejectedText?: React.ReactNode;

  /** The accepted file extensions for the Drag-and-Drop. */
  acceptedFileExtensions?: string[];
};

type DropzoneLoadingProgressBarProps = {
  /**  The percentage of the progress bar. */
  progressBarPrecentage: number;

  /** The time in minute left to upload the file. */
  progressBarMinute?: number;
};

/**
 *
 *
 * Helpers
 *
 *
 */

const browser = detect();

const isSafari = browser?.name === 'safari';

const DropzoneComponent = ({
  dropZoneOptions,
  supportedFilesDescription,
  loadingElement,
  heading,
  rejectedText,
  acceptedFileExtensions,
  htmlAttributes,
  style,
  className,
  ref,
  ...restProps
}: CommonProps<'div', DropzoneProps>) => {
  /**
   * We intercept the onDrop function in order to enforce
   * custom file extensions. Before calling the onDrop props we check
   * if the custom file extensions is acceptable. If the extension
   * conforms to acceptedFileExtensions, we call onDrop. Otherwise, we
   * present an error message.
   */
  function onDropInterceptor<T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
    event: DropEvent,
  ) {
    if (acceptedFileExtensions && acceptedFileExtensions.length > 0) {
      for (const file of acceptedFiles) {
        const isAccepted = acceptedFileExtensions.some((extension) => {
          if (file.name.endsWith(extension)) {
            return true;
          }
        });
        if (!isAccepted) {
          setIsRejected(true);
          return;
        }
      }
    }
    setIsRejected(false);
    dropZoneOptions.onDrop?.(acceptedFiles, fileRejections, event);
  }

  const [isRejected, setIsRejected] = useState(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([dropZoneRef, ref]);
  const isDisabled = dropZoneOptions?.disabled || Boolean(loadingElement);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    ...dropZoneOptions,
    disabled: isDisabled,
    noClick: true,
    onDrop: onDropInterceptor,
  });

  const { width } = useResizeObserver({
    ref: dropZoneRef as RefObject<HTMLElement>,
  });

  const isUploading = Boolean(loadingElement);

  const titleText =
    isDragActive && isDragReject
      ? 'Format not supported'
      : isDragActive && !isDragReject
        ? 'Drop file'
        : null;

  const computedFileImage =
    (isDragActive && isDragAccept) || isUploading ? (
      <DocumentArrowUpIconSolid className="n-size-token-48 n-text-primary-bg-strong" />
    ) : (
      <XCircleIconSolid className="n-size-token-48 n-text-danger-bg-strong" />
    );

  const wrapperClasses = classNames('ndl-dropzone', className, {
    'ndl-drag-active': isDragActive,
    'ndl-drag-disabled': dropZoneOptions?.disabled,
    'ndl-file-invalid': isDragActive && isDragReject,
  });

  useMemo(() => {
    if (isDragActive && isRejected) {
      setIsRejected(false);
    }
  }, [isDragActive, isRejected]);

  return (
    <>
      {isRejected && (
        <Banner className="ndl-dropzone-error-alert" variant="danger">
          <Banner.Header>Error</Banner.Header>
          <Banner.Description>
            {Boolean(rejectedText) || 'File extension not supported.'}
          </Banner.Description>
        </Banner>
      )}
      <div
        ref={mergedRef}
        className={wrapperClasses}
        style={style}
        {...restProps}
        {...htmlAttributes}
      >
        <div className="ndl-dropzone-inner-wrapper" {...getRootProps()}>
          <input aria-label="Drag and drop area" {...getInputProps()} />
          <div className="ndl-dropzone-inner">
            {/* Illustration should be visible only after arbitrary width */}
            {width !== undefined && width > 230 && (
              <>
                {isDragActive && !isSafari ? (
                  computedFileImage
                ) : (
                  <DocumentArrowUpIconOutline className="ndl-dropzone-upload-icon" />
                )}
              </>
            )}
            {!isUploading && (
              <div className="ndl-dnd-title-container">
                <Typography variant="subheading-medium">
                  {titleText ||
                    (heading ?? (
                      <>
                        Drag <span className="n-font-light">&</span> Drop
                      </>
                    ))}
                </Typography>
                {!isDragReject && (
                  <div className="ndl-dnd-subtitle">
                    <Typography variant="body-medium">or</Typography>{' '}
                    <button
                      className="ndl-dnd-browse-link"
                      onClick={() => open()}
                      disabled={isDisabled}
                      type="button"
                    >
                      Browse
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="ndl-dropzone-footer">
              {isUploading && <>{loadingElement}</>}
              {!isUploading && (
                <div className="ndl-file-support-text">
                  {supportedFilesDescription}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const DropzoneLoadingProgressBarComponent = ({
  progressBarPrecentage,
  progressBarMinute,
  htmlAttributes,
  className,
  style,
  ref,
  ...restProps
}: CommonProps<'div', DropzoneLoadingProgressBarProps>) => {
  return (
    <div className="ndl-dropzone-loading-progress-bar-wrapper">
      <div className="ndl-dropzone-loading-progress-bar-indicators">
        <Typography
          className="ndl-dropzone-loading-progress-bar-precentage"
          variant="subheading-small"
        >
          {`${progressBarPrecentage}%`}
        </Typography>
        {Boolean(progressBarMinute) && (
          <Typography
            className="ndl-dropzone-loading-progress-bar-minute"
            variant="body-medium"
          >{`(${progressBarMinute} minutes left)`}</Typography>
        )}
      </div>
      <ProgressBar
        className={classNames('ndl-dropzone-loading-progress-bar', className)}
        value={progressBarPrecentage}
        size="large"
        htmlAttributes={htmlAttributes}
        ref={ref}
        style={style}
        {...restProps}
      />
    </div>
  );
};

const Dropzone = Object.assign(DropzoneComponent, {
  LoadingProgressBar: DropzoneLoadingProgressBarComponent,
});

export { Dropzone };
